import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { AuthStrategy } from './types';

/**
 * Marca de "esta request ya es el reintento tras un refresh".
 *
 * axios 0.19.0 (`lib/core/mergeConfig.js`) descarta las claves de config que no conoce al hacer
 * `http(originalRequest)`, así que `_retry` no sobrevivía al reintento: si el backend respondía 401 también con
 * el token renovado, se refrescaba y reintentaba sin fin. Los headers sí se conservan, por eso la marca viaja
 * como header y el interceptor de request la convierte en `_retry` sobre la config final (la misma que vuelve
 * en `error.config`) y la quita antes de enviar: nunca llega al backend.
 */
export const RETRY_MARKER_HEADER = 'X-Auth-Retry-Marker';

export type RetriableRequestConfig = AxiosRequestConfig & { _retry?: boolean };

/** Copia de la request marcada como reintento (ver `RETRY_MARKER_HEADER`); no muta la config original. */
export const markAsRetry = (config: RetriableRequestConfig): RetriableRequestConfig => ({
    ...config,
    _retry: true,
    headers: { ...(config.headers || {}), [RETRY_MARKER_HEADER]: '1' }
});

/** Convierte la marca en `_retry` sobre la config final y la quita del header (idempotente). */
export const consumeRetryMarker = (config: RetriableRequestConfig): RetriableRequestConfig => {
    if (config.headers && config.headers[RETRY_MARKER_HEADER]) {
        delete config.headers[RETRY_MARKER_HEADER];
        config._retry = true;
    }
    return config;
};

export const isUnauthorized = (error: AxiosError): boolean => !!(error && error.response && error.response.status === 401);

/**
 * Instala UNA vez los interceptores de auth sobre `http`. `resolveStrategy` devuelve la estrategia que corresponde
 * en el momento de cada request / respuesta (en la app, la de la ruta activa de `AccessCoordinator`).
 * - request: consume la marca de reintento y delega en `applyAuthorization`;
 * - respuesta 401: delega en `handleUnauthorized`; cualquier otro error se rechaza tal cual.
 */
export const installAuthInterceptors = (http: AxiosInstance, resolveStrategy: () => AuthStrategy | null): void => {
    http.interceptors.request.use(
        (config: AxiosRequestConfig) => {
            consumeRetryMarker(config);
            const strategy = resolveStrategy();
            return strategy ? strategy.applyAuthorization(config) : config;
        },
        err => Promise.reject(err)
    );

    http.interceptors.response.use(
        response => response,
        (error: AxiosError) => {
            const strategy = resolveStrategy();
            if (strategy && isUnauthorized(error)) {
                return strategy.handleUnauthorized(error, http);
            }
            return Promise.reject(error);
        }
    );
};
